import { useWebsocketRoom } from 'infrastructure/hooks/websockets/use-room';
import { useAppDispatch, useAppSelector } from 'store';
import CustomLocationPrompt from 'components/molecules/custom-location-prompt';
import { ASSESSMENT_PREFIX } from 'infrastructure/consts/assessment-modal';
import useUser from 'utils/useUser';
import { Navigate } from 'react-router-dom';
import { Routes } from 'router/routes';
import {
  PLATFORM_MODE_ADMIN_PANEL_ACCESS,
  PLATFORM_MODE_PATIENT_ACCEPTANCE,
} from 'utils/accessManager/constants';
import BaseGrid from 'components/atoms/base-grid';
import DashboardLayout from 'components/templates/dashboard-layout';
import RpmOverviewDashboard from 'components/organisms/rpm-overview-dashboard';
import OverviewDashboardStatusCountFilter from 'components/organisms/rpm-overview-dashboard-status-count';
import ActiveFiltersBar from 'components/molecules/active-filters-bar';
import {
  type FC,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard';
import { endOfDay, startOfDay, startOfMonth } from 'date-fns';
import { timePresetKeys, PresetKeyEnum } from 'infrastructure/enums';

import s from './styles.module.scss';

import type { PresetKeyEnum as PresetKeyEnumType } from 'infrastructure/enums';

export const Page: FC = () => {
  const { filter, updateFilter, count, resetFilter } =
    useOverviewDashboardFilter();

  const [selectedPatientName, setSelectedPatientName] = useState<string | null>(
    null,
  );
  const [selectedClinicName, setSelectedClinicName] = useState<string | null>(
    null,
  );
  const [selectedAgencyDoctorName, setSelectedAgencyDoctorName] = useState<
    string | null
  >(null);
  const [hasAppliedTimeframe, setHasAppliedTimeframe] = useState(false);
  const [timeframePresetKey, setTimeframePresetKey] = useState<string | null>(
    null,
  );
  const [filterOrder, setFilterOrder] = useState<string[]>([]);
  const prevFilterRef = useRef(filter);

  const actions = useAppSelector((state) => state.activityActions.actions);
  const {
    activityActions: { resetActions },
  } = useAppDispatch();
  const hasUnsavedMessage = Object.values(actions).some((item) =>
    Boolean(item?.message?.length),
  );

  useWebsocketRoom('rpmDashboard');

  const promptCallback = () => {
    resetActions();
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(`${ASSESSMENT_PREFIX}-`)) {
        localStorage.removeItem(key);
      }
    });
  };

  const dateValues: [Date, Date] | undefined = useMemo(() => {
    if (!filter.startTime || !filter.endTime) return undefined;

    try {
      return [new Date(filter.startTime), new Date(filter.endTime)];
    } catch (error) {
      return undefined;
    }
  }, [filter.startTime, filter.endTime]);

  const onDateChange = useCallback(
    (
      dates: [Date | null, Date | null],
      dateString: [string, string],
      info: { range?: 'start' | 'end' },
      presetKey?: string,
    ) => {
      const [startDate, endDate] = dates;

      if (startDate === null && endDate === null) {
        updateFilter({
          startTime: startOfMonth(new Date()).getTime(),
          endTime: endOfDay(new Date()).getTime(),
          page: 1,
        });
        setHasAppliedTimeframe(false);
        setTimeframePresetKey(PresetKeyEnum.CurrentMonth);
        return;
      }

      const isTimePreset =
        presetKey && timePresetKeys.includes(presetKey as PresetKeyEnumType);

      if (startDate && endDate) {
        updateFilter({
          startTime: (isTimePreset
            ? startDate
            : startOfDay(startDate)
          ).getTime(),
          endTime: (isTimePreset ? endDate : endOfDay(endDate)).getTime(),
          page: 1,
        });
        const isCurrentMonth = presetKey === PresetKeyEnum.CurrentMonth;
        setHasAppliedTimeframe(!isCurrentMonth);
        setTimeframePresetKey(presetKey || null);
      } else {
        if (startDate && info.range === 'start') {
          updateFilter({
            startTime: (isTimePreset
              ? startDate
              : startOfDay(startDate)
            ).getTime(),
            page: 1,
          });
        }
        if (endDate && info.range === 'end') {
          updateFilter({
            endTime: (isTimePreset ? endDate : endOfDay(endDate)).getTime(),
            page: 1,
          });
        }
      }
    },
    [updateFilter],
  );

  const handleFilterChange = useCallback(
    (key: string, value: any) => {
      updateFilter({ [key]: value, page: 1 });
    },
    [updateFilter],
  );

  const filterValues = useMemo(
    () => ({
      patientGuid: filter.patientGuid,
      clinicGuid: filter.clinicGuid,
    }),
    [filter.patientGuid, filter.clinicGuid],
  );

  const handleRemoveFilter = useCallback(
    (key: keyof typeof filter) => {
      if (key === 'startTime' || key === 'endTime') {
        updateFilter({
          startTime: startOfMonth(new Date()).getTime(),
          endTime: endOfDay(new Date()).getTime(),
          page: 1,
        });
        setHasAppliedTimeframe(false);
        setTimeframePresetKey(PresetKeyEnum.CurrentMonth);
        setFilterOrder((prev) => prev.filter((k) => k !== 'timeframe'));
      } else {
        updateFilter({ [key]: undefined, page: 1 });
        if (key === 'patientGuid') {
          setSelectedPatientName(null);
        }
        if (key === 'clinicGuid') {
          setSelectedClinicName(null);
        }
        if (key === 'actionedByAgencyDoctorGuid') {
          setSelectedAgencyDoctorName(null);
        }
        setFilterOrder((prev) => prev.filter((k) => k !== key));
      }
    },
    [updateFilter],
  );

  const handlePatientLoaded = useCallback((patientName: string | null) => {
    setSelectedPatientName(patientName);
  }, []);

  const handleClinicLoaded = useCallback((clinicName: string | null) => {
    setSelectedClinicName(clinicName);
  }, []);

  const handleAgencyDoctorLoaded = useCallback(
    (agencyDoctorName: string | null) => {
      setSelectedAgencyDoctorName(agencyDoctorName);
    },
    [],
  );

  const handleResetFilter = useCallback(() => {
    resetFilter();
    setSelectedPatientName(null);
    setSelectedClinicName(null);
    setSelectedAgencyDoctorName(null);
    setHasAppliedTimeframe(false);
    setTimeframePresetKey(null);
    setFilterOrder([]);
  }, [resetFilter]);

  useEffect(() => {
    const prevFilter = prevFilterRef.current;
    const newlyChangedKeys: string[] = [];

    Object.keys(filter).forEach((key) => {
      const typedKey = key as keyof typeof filter;
      if (
        typedKey !== 'page' &&
        typedKey !== 'sorting' &&
        filter[typedKey] !== prevFilter[typedKey]
      ) {
        if (typedKey === 'startTime' || typedKey === 'endTime') {
          if (!newlyChangedKeys.includes('timeframe')) {
            newlyChangedKeys.push('timeframe');
          }
        } else {
          newlyChangedKeys.push(typedKey);
        }
      }
    });

    if (newlyChangedKeys.length > 0) {
      setFilterOrder((prevOrder) => {
        const filteredOrder = prevOrder.filter(
          (key) => !newlyChangedKeys.includes(key),
        );
        return [...filteredOrder, ...newlyChangedKeys];
      });
    }

    prevFilterRef.current = filter;
  }, [filter]);

  return (
    <DashboardLayout
      dateValues={dateValues}
      onDateChange={onDateChange}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      onPatientLoaded={handlePatientLoaded}
      onClinicLoaded={handleClinicLoaded}
      onAgencyDoctorLoaded={handleAgencyDoctorLoaded}
      filterCount={count}
      showRpmHeaderActions
      filter={filter}
      updateFilter={updateFilter}
      resetFilter={handleResetFilter}
    >
      <CustomLocationPrompt
        when={hasUnsavedMessage}
        callback={promptCallback}
      />
      <ActiveFiltersBar
        filter={filter}
        resetFilter={handleResetFilter}
        onRemoveFilter={handleRemoveFilter}
        patientName={selectedPatientName}
        clinicName={selectedClinicName}
        agencyDoctorName={selectedAgencyDoctorName}
        hasAppliedTimeframe={hasAppliedTimeframe}
        timeframePresetKey={timeframePresetKey}
        filterOrder={filterOrder}
      />
      <div>
        <BaseGrid className={s.wrapper} rowGap={30}>
          <OverviewDashboardStatusCountFilter />
          <RpmOverviewDashboard />
        </BaseGrid>
      </div>
    </DashboardLayout>
  );
};

const OverviewDashboardPage = () => {
  const { isTurkUserRole, guid, platformMode, isPatient } = useUser();

  if (isPatient) return <Navigate to={Routes.patientGuid(guid)} replace />;
  if (isTurkUserRole) return <Navigate to={Routes.turkDashboard} replace />;
  if (
    platformMode === PLATFORM_MODE_PATIENT_ACCEPTANCE ||
    platformMode === PLATFORM_MODE_ADMIN_PANEL_ACCESS
  )
    return <Navigate to={Routes.adminPatients} replace />;

  return <Page />;
};

export default OverviewDashboardPage;
